import React from 'react';
import { Link } from 'react-router-dom';

const LegalPage: React.FC = () => {
  return (
    <div className="bg-gray-950 text-white min-h-screen py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-white mb-4">Legal Information</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Our commitment to transparency, compliance, and protecting your data and business interests.
          </p>
        </div>
        
        {/* Legal Documents */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Legal Documents</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-colors">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Terms of Service</h3>
              <p className="text-gray-400 mb-4">
                Our terms and conditions governing the use of BarnBills software and services.
              </p>
              <Link to="/terms" className="inline-flex items-center text-blue-400 hover:text-blue-300 font-medium transition-colors">
                Read Terms of Service
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                </svg>
              </Link>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-colors">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Privacy Policy</h3>
              <p className="text-gray-400 mb-4">
                How we collect, use, and protect your personal and business information.
              </p>
              <Link to="/privacy" className="inline-flex items-center text-blue-400 hover:text-blue-300 font-medium transition-colors">
                Read Privacy Policy
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* Compliance */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Compliance & Certifications</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-gray-300">SOC 2 Type II Certified</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-gray-300">PCI DSS Compliant</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-gray-300">GDPR Compliant</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-gray-300">CCPA Compliant</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span className="text-gray-300">PIPEDA Compliant</span>
              </div>
            </div>
          </div>
        </section>

        {/* Data Protection */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Data Protection</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                <span className="text-gray-300">End-to-end encryption for all data transmission and storage</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                <span className="text-gray-300">Regular security audits and penetration testing by third-party experts</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                <span className="text-gray-300">Data residency options available for international customers</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                <span className="text-gray-300">Right to data portability - export your data anytime</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                <span className="text-gray-300">Right to deletion upon request with complete data removal</span>
              </div>
            </div>
          </div>
        </section>

        {/* Business Terms */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Business Terms</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                <span className="text-gray-300">30-day money-back guarantee on all subscriptions</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                <span className="text-gray-300">No long-term contracts required - month-to-month flexibility</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                <span className="text-gray-300">Cancel anytime policy with immediate effect</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                <span className="text-gray-300">Transparent pricing with no hidden fees or surprise charges</span>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                <span className="text-gray-300">Service level agreements (SLA) available for enterprise customers</span>
              </div>
            </div>
          </div>
        </section>

        {/* Contact for Legal Matters */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Legal Contact</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
            <p className="text-gray-400 mb-6">
              For legal inquiries, compliance questions, or to report security issues:
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h4 className="text-white font-medium mb-3">General Legal</h4>
                <a href="mailto:legal@barnbills.com" className="text-blue-400 hover:text-blue-300 transition-colors">
                  legal@barnbills.com
                </a>
              </div>
              <div>
                <h4 className="text-white font-medium mb-3">Security Issues</h4>
                <a href="mailto:security@barnbills.com" className="text-blue-400 hover:text-blue-300 transition-colors">
                  security@barnbills.com
                </a>
              </div>
              <div>
                <h4 className="text-white font-medium mb-3">Privacy Questions</h4>
                <a href="mailto:privacy@barnbills.com" className="text-blue-400 hover:text-blue-300 transition-colors">
                  privacy@barnbills.com
                </a>
              </div>
              <div>
                <h4 className="text-white font-medium mb-3">Data Requests</h4>
                <a href="mailto:data@barnbills.com" className="text-blue-400 hover:text-blue-300 transition-colors">
                  data@barnbills.com
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-8">Document Updates</h2>
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8">
            <div className="space-y-3">
                                            <div className="flex justify-between items-center border-b border-gray-800 pb-2">
                 <span className="text-gray-300">Terms of Service</span>
                 <span className="text-gray-500 text-sm">Last updated May 20, 2025</span>
               </div>
               <div className="flex justify-between items-center border-b border-gray-800 pb-2">
                 <span className="text-gray-300">Privacy Policy</span>
                 <span className="text-gray-500 text-sm">Last updated June 8, 2025</span>
               </div>
               <div className="flex justify-between items-center">
                 <span className="text-gray-300">Legal Information</span>
                 <span className="text-gray-500 text-sm">Last updated June 8, 2025</span>
               </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LegalPage; 